/**
 * Super-admin · Atlas errors.
 *
 * Failed real-coding runs that haven't been resolved by a later success — which
 * ticket, whom it's assigned to, and the exact error. "Try again with Atlas"
 * re-runs the ticket feeding the error in, so the coder can diagnose + avoid it;
 * once a retry succeeds the ticket drops off this page.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";

import { api, extractErrorMessage } from "@/lib/api";
import type { AtlasError } from "@/lib/types";

export function AtlasErrorsPage() {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["atlas-errors"],
    queryFn: async () => (await api.get<AtlasError[]>("/superadmin/atlas-errors")).data,
    refetchInterval: 15_000,
  });
  const errors = query.data ?? [];

  const retry = useMutation({
    mutationFn: async (runId: string) =>
      (await api.post(`/superadmin/atlas-errors/${runId}/retry`)).data,
    onSuccess: () => {
      setErr(null);
      setMsg("Retry started — Atlas is re-running the ticket with the error in context.");
      queryClient.invalidateQueries({ queryKey: ["atlas-errors"] });
    },
    onError: (e) => {
      setMsg(null);
      setErr(extractErrorMessage(e, "Could not start the retry."));
    },
  });

  return (
    <div style={{ maxWidth: 980 }}>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Atlas errors
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Coding runs that failed and haven't been fixed by a later success — the ticket, the assignee,
          and why it failed. Retry re-runs the ticket with the error in context; a successful retry
          clears it from this list.
        </p>
      </header>

      {msg && <div style={okBar}>{msg}</div>}
      {err && <div role="alert" style={errBar}>{err}</div>}

      {query.isLoading ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : errors.length === 0 ? (
        <div style={emptyCard}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>No unresolved errors</span>
          <span style={{ color: "var(--mute)", fontSize: 13 }}>
            Every attempted ticket has either succeeded or been retried clean.
          </span>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {errors.map((e) => {
            const isOpen = open === e.run_id;
            const retrying = e.state === "retrying" || (retry.isPending && retry.variables === e.run_id);
            return (
              <div key={e.run_id} style={card}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={statusPill(retrying)}>
                    <span style={{ width: 6, height: 6, borderRadius: 9999, background: retrying ? "#c26a00" : "#e5484d" }} />
                    {retrying ? "Retrying" : "Failed"}
                  </span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--ink)" }}>
                    {e.issue_key ?? "—"}
                  </span>
                  <span style={{ flex: 1, fontSize: 14, minWidth: 200 }}>{e.issue_title ?? "(untitled)"}</span>
                  {e.project_key && (
                    <span style={tag}>{e.project_key}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => retry.mutate(e.run_id)}
                    disabled={retrying}
                    style={retryBtn(retrying)}
                  >
                    {retrying ? "Retrying…" : "Try again with Atlas"}
                  </button>
                </div>

                <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 12.5, color: "var(--body-mid)", flexWrap: "wrap" }}>
                  <span>
                    Assigned to <strong style={{ color: "var(--ink)" }}>{e.assignee_name ?? "—"}</strong>
                    {e.assignee_email ? ` · ${e.assignee_email}` : ""}
                  </span>
                  {e.tenant_name && <span>Tenant: {e.tenant_name}</span>}
                  {e.failed_at && <span>Failed {new Date(e.failed_at).toLocaleString()}</span>}
                </div>

                {e.error && (
                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? null : e.run_id)}
                      style={disclosure}
                    >
                      {isOpen ? "▾ Hide error" : "▸ Show error"}
                    </button>
                    {isOpen && <pre style={errorBox}>{e.error}</pre>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const card: CSSProperties = {
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 8, padding: 16,
};
const emptyCard: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start",
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 8, padding: 24,
};
const tag: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--mute)",
  background: "var(--surface-2)", padding: "2px 7px", borderRadius: 3,
};
function statusPill(retrying: boolean): CSSProperties {
  return {
    display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 500,
    padding: "3px 9px", borderRadius: 9999,
    color: retrying ? "#8a4b00" : "#b42318",
    background: retrying ? "rgba(194,106,0,0.10)" : "rgba(229,72,77,0.10)",
  };
}
function retryBtn(disabled: boolean): CSSProperties {
  return {
    background: disabled ? "var(--surface-2)" : "var(--ink)",
    color: disabled ? "var(--mute)" : "var(--on-primary)",
    border: "none", borderRadius: 4, padding: "7px 13px", fontSize: 12.5, fontWeight: 500,
    cursor: disabled ? "default" : "pointer",
  };
}
const disclosure: CSSProperties = {
  background: "none", border: "none", padding: 0, cursor: "pointer",
  color: "var(--body-mid)", fontSize: 12,
};
const errorBox: CSSProperties = {
  margin: "8px 0 0", padding: 12, background: "var(--surface-1)", border: "1px solid var(--hairline)",
  borderRadius: 6, fontFamily: "var(--font-mono)", fontSize: 11.5, lineHeight: 1.5,
  whiteSpace: "pre-wrap", wordBreak: "break-word", color: "var(--ink)", maxHeight: 320, overflow: "auto",
};
const okBar: CSSProperties = {
  padding: "9px 12px", fontSize: 13, color: "#047a18", marginBottom: 14,
  background: "rgba(0,215,34,0.08)", border: "1px solid rgba(0,215,34,0.3)", borderRadius: 6,
};
const errBar: CSSProperties = {
  padding: "9px 12px", fontSize: 13, color: "#ee1d36", marginBottom: 14,
  background: "rgba(238,29,54,0.06)", border: "1px solid rgba(238,29,54,0.3)", borderRadius: 6,
};
